import React, { FC } from 'react';
import produce from 'immer';
import { get, merge } from 'lodash';
import { Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import FormDrawer from '~/components/FormDrawer';
import { Form } from '~/components';
import { useValidator } from '~/controllers/validation';
import { useGetApplication, useGetPayload } from '~/controllers/wizard';
import { useGetDraft } from '~/controllers/draft';
import { Draft } from '~/types/Draft';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import { getStagePayload } from '~/utils/getStagePayload';
import { FormItemContextProvider } from '~/components/Form/FormItemContext';
import { isReadOnly } from '~/utils/isReadOnly';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import { KsaShareholders } from '~/types/ksa/KsaShareholders';
import PersonReviewFormItems from '~/pages/ksa/review/managers/components/PersonReviewFormItems';
import {
  getIndividualsFormName,
  hasAnyDeclinedField,
} from '~/pages/ksa/application/shareholders/components/individuals/IndividualEdit';
import ReviewItem from '~/pages/ksa/review/components/ReviewItem';
import { KsaIndividualShareholder } from '~/types/ksa/KsaIndividualShareholder';
import { KsaPerson } from '~/types/ksa/KsaPerson';
import { Stage } from '~/types/Application';
import { isEditButtonDisabled } from '~/components/views/ShareholdersMultiple/utils';
import { detectFieldIssue } from '~/utils/detectFieldIssue';

interface Props extends Partial<FormItemInjectedProps<KsaShareholders>> {
  uid: string;
  onClose: () => void;
}

const IndividualReview: FC<Props> = (props) => {
  const { uid, onClose } = props;
  const { value, onChange } = props as FormItemInjectedProps<KsaShareholders>;
  const { t } = useTranslation('ksaReview');
  const reviewShareholdersFormState = { reviewShareholders: value };
  const applicationQuery = useGetApplication();
  const payload = useGetPayload();

  const currentIndex =
    (payload?.shareholders as KsaShareholders)?.individuals?.findIndex(
      (item) => item?.uid === uid
    ) || 0;

  const onFinish = (newValues: ApplicationPayload) => {
    const updatedData = produce(reviewShareholdersFormState, (draft) => {
      merge(draft, newValues);
    });
    onChange?.(updatedData.reviewShareholders);
    onClose();
  };

  const withContinueButton = !isEditButtonDisabled(
    applicationQuery.data,
    hasAnyDeclinedField(applicationQuery?.data)
  );

  return (
    <FormDrawer
      id="individualReviewDrawer"
      visible={!!uid}
      onClose={onClose}
      title={t('Individual Shareholder')}
      formName={formName}
      withContinueButton={withContinueButton}
    >
      <IndividualForm
        index={currentIndex}
        onFinish={onFinish}
        initialValues={reviewShareholdersFormState}
      />
    </FormDrawer>
  );
};

interface IndividualFormProps {
  index: number;
  onFinish: (values: ApplicationPayload) => any;
  initialValues?: any | undefined;
}

const IndividualForm: FC<IndividualFormProps> = (props) => {
  const { onFinish, initialValues, index } = props;
  const { t } = useTranslation('ksaReview');

  const [form] = Form.useForm<ApplicationPayload>();
  const getFormState = () => {
    return produce(initialValues || {}, (draft: any) => {
      merge(draft, form.getFieldsValue());
    });
  };
  const validatorController = useValidator(getFormState, undefined, Stage.ksaApplication);
  const applicationQuery = useGetApplication();
  const draftQuery = useGetDraft<Draft<any>>();

  const optionalFieldsController = useOptionalFields({ initialValues });

  const stagePayload = getStagePayload.wizard(applicationQuery.data);

  const nameArr = getReviewIndividualsShareholderFormName(index);

  const payload = useGetPayload();
  const individualShareholder: KsaIndividualShareholder | undefined = get(
    payload,
    getIndividualsFormName(index)
  );
  const individualFromProvided: KsaPerson | undefined = get(
    payload?.provided,
    getIndividualsFormName(index)
  );

  return (
    <FormItemContextProvider
      value={{
        defaultValidator: validatorController.validator,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields || {},
        errorFields: undefined,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabledByDefault: isReadOnly(applicationQuery.data),
        disabled: isReadOnly(applicationQuery.data),
      }}
    >
      <Form
        form={form}
        name={formName}
        onFinish={onFinish}
        onValuesChange={(_: any, newValues: any) => {
          optionalFieldsController.debouncedUpdate(newValues);
        }}
        initialValues={initialValues}
      >
        <PersonReviewFormItems
          formItemsPath={nameArr}
          payloadReferencePath={getIndividualsFormName(index)}
          hasPassport={!!individualFromProvided?.passport?.documentNumber}
          hasPersonalDocument={!!individualFromProvided?.personalDocument?.idNumber}
          isSignedWithEmail={!!individualFromProvided?.personalData?.email}
          hasVerifiedPhoneNumber={
            !!individualFromProvided?.personalData?.phoneNumber ||
            !!individualShareholder?.personalData?.phoneNumber
          }
        />
        <section>
          <Typography.Title level={5}>{t('Ownership share:').toUpperCase()}</Typography.Title>
          <InputsGroupLayout>
            {/* Shares % and count are from Thiqah; editable when they are missing or returned as Arabic strings.
                Share Type and Entity Relationship Type are Risk Team fields not in the applicant form — always manual entry. */}
            <Form.Item
              labelText={t('Shares %')}
              name={[...nameArr, 'ownershipShare', 'agentPercent']}
              initialValue={individualShareholder?.ownershipShare?.percent || undefined}
            >
              <Input type="number" placeholder={t('Enter shares %')} />
            </Form.Item>
            <Form.Item
              labelText={t('Shares count')}
              name={[...nameArr, 'ownershipShare', 'agentCount']}
              initialValue={individualShareholder?.ownershipShare?.count || undefined}
            >
              <Input type="number" placeholder={t('Enter shares count')} />
            </Form.Item>
            <Form.Item
              labelText={t('Share Type')}
              name={[...nameArr, 'ownershipShare', 'agentShareType']}
              initialValue={(payload?.thiqahFullInfo as any)?.parties?.[index]?.partnership?.[0]?.name || undefined}
            >
              <Input placeholder={t('Enter share type')} />
            </Form.Item>
            {/* Entity Relationship Type is not in the applicant form; always a manual agent entry */}
            <Form.Item
              labelText={t('Entity Relationship Type')}
              name={[...nameArr, 'agentEntityRelationshipType']}
              initialValue={undefined}
            >
              <Input placeholder={t('Enter entity relationship type')} />
            </Form.Item>
            <Form.Item
              labelText={t('Share Value')}
              name={[...nameArr, 'ownershipShare', 'agentShareValue']}
              initialValue={undefined}
            >
              <Input type="number" placeholder={t('Enter share value')} />
            </Form.Item>
          </InputsGroupLayout>
        </section>
      </Form>
    </FormItemContextProvider>
  );
};

const formName = 'individuals';
const getReviewIndividualsShareholderFormName = (index: number) => [
  'reviewShareholders',
  formName,
  index,
];

export default IndividualReview;
